import { Button } from '@chakra-ui/button'
import { Flex, Stack } from '@chakra-ui/react'
import type { TravelInsuredDto, WizardTravelAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useNameof } from '@/hooks/useNameof'
import { useSaveAdditionalData, useUpdateLastWizardStep } from '@/hooks/useWizard'

import { FormSection } from '../../../forms/FormSection'
import { InsureesForm } from '../Step1/forms/InsureesForm'
import { AdditionalPolicyHolderTravelInfoForm } from './forms/AdditionalPolicyHolderTravelInfoForm'
import { AdditionalVehicleAssistanceInfoForm } from './forms/AdditionalVehicleAssistanceInfoForm'

export const Step3Sections = {
  pojistnik: 1,
  persons: 2,
  vehicle: 3
}
export type Step3Sections = typeof Step3Sections[keyof typeof Step3Sections]

export type Step3FormValues = WizardTravelAdditionalData

export type Step3FormProps = {
  data?: Step3FormValues
}

export const transformInsurees = (insurees: TravelInsuredDto[] | undefined) => {
  insurees?.forEach(x => x.uid = global.newUid())

  return insurees
}

export const transformStep3FormValues = (values: Step3FormValues) => {
  return {
    ...values,
    insurees: transformInsurees(values.insurees)
  } as Step3FormValues
}

export const Step3 = ({ data }: Step3FormProps) => {
  const navigate = useNavigate()
  const { insuranceParams, refetchInsuranceParams, insuranceParamsId: paramsId, isDisabled } = useInsuranceParams()
  const { mutateAsync: saveAdditionalInfo } = useSaveAdditionalData()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()

  const formDefaultValues: Step3FormValues = {
    ...transformStep3FormValues(data!),
    nextPage: 'next'
  }

  const methods = useForm<Step3FormValues>({
    values: formDefaultValues,
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder)
  const insureesPrefix = nameof(x => x.insurees)

  // v nevalidniho formulare se nascroluje na validationSummary
  const validationSummaryRef = useRef<HTMLDivElement>(null)
  const scrollToValidationSummary = useCallback(() => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }, [validationSummaryRef])

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    setError,
    getValues,
    reset,
    watch
  } = methods

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.TravelInsurance, 3, paramsId, methods)

  const submitForm = handleSubmit((data) => {
    const values = cloneDeep(data)

    return saveAdditionalInfo(values)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        if (values.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 3) == 3) {
            updateStep({ paramsId: Number(paramsId), step: 4 })
              .then(() => refetchInsuranceParams())
          }

          setTimeout(() => {
            navigate(`/travel/steps/4/${paramsId}`) // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
          }, 0)
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
      .finally(() => { })
  }, (err) => {
    scrollToValidationSummary()
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>
          <CustomWaypoint sectionId={`section_${Step3Sections.pojistnik}`}>
            <PageSection id={`section_${Step3Sections.pojistnik}`}>
              <FormSection number={Step3Sections.pojistnik} sectionTitle="Pojistník">
                <AdditionalPolicyHolderTravelInfoForm personPrefix={personPrefix} />
              </FormSection>
            </PageSection>
          </CustomWaypoint>

          <CustomWaypoint sectionId={`section_${Step3Sections.persons}`}>
            <PageSection id={`section_${Step3Sections.persons}`} >
              <FormSection number={Step3Sections.persons} sectionTitle="Pojištěné osoby">
                <InsureesForm editableBirthnumber prefix={insureesPrefix} />
              </FormSection>
            </PageSection>
          </CustomWaypoint >

          {data?.vehicleData?.inputRequired &&
            <CustomWaypoint sectionId={`section_${Step3Sections.vehicle}`}>
              <PageSection id={`section_${Step3Sections.vehicle}`} >
                <FormSection number={Step3Sections.vehicle} sectionTitle="Vozidlo">
                  <AdditionalVehicleAssistanceInfoForm />
                </FormSection>
              </PageSection>
            </CustomWaypoint >
          }

          {!isDisabled &&
            < Flex justifyContent='center'>
              <Button
                type="button"
                variant="solid"
                colorScheme="green"
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Pokračovat
              </Button>
            </Flex>
          }

          <ErrorSummary errors={errors} ref={validationSummaryRef} />
          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Stack>
      </form >
    </FormProvider >
  )
}
